import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, functions } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import "./MyOffers.css";

const norm = (v) => String(v || "").trim();
const lower = (v) => norm(v).toLowerCase();

const toMillis = (v) => {
  try {
    if (!v) return 0;
    if (typeof v.toMillis === "function") return v.toMillis();
    if (typeof v.toDate === "function") return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    if (typeof v === "number") return v;
    const d = new Date(String(v));
    return Number.isFinite(d.getTime()) ? d.getTime() : 0;
  } catch {
    return 0;
  }
};

const fmtDate = (v) => {
  const ms = toMillis(v);
  if (!ms) return "Unknown date";

  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(ms).toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "â€”";

  try {
    return n.toLocaleString("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 0,
    });
  } catch {
    return `R${n.toLocaleString("en-ZA")}`;
  }
};

const getTradeIdFromOffer = (offer) =>
  norm(
    offer?.tradeId ||
      offer?.requestedItemId ||
      offer?.tradeItemId ||
      offer?.itemId ||
      ""
  );

const getOfferStatus = (offer) => {
  const s = lower(offer?.status || offer?.offerStatus || offer?.state || "pending");

  if (!s) return "pending";
  if (s.includes("blocked_low") || s.includes("low_offer")) return "blocked_low_offer";
  if (s.includes("accept")) return "accepted";
  if (s.includes("declin")) return "declined";
  if (s.includes("withdraw")) return "withdrawn";
  if (s.includes("cancel")) return "cancelled";
  if (s.includes("expir")) return "expired";
  if (s.includes("pend")) return "pending";

  return s;
};

const getOfferType = (offer) => lower(offer?.offerType || offer?.type || "offer");

const getCashAmount = (offer) => {
  const raw =
    offer?.proposedCashAmount ??
    offer?.cashAmount ??
    offer?.amount ??
    offer?.cash ??
    null;

  const n = Number(String(raw ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const getImageFromTrade = (trade) => {
  if (!trade) return "";

  return (
    trade.primaryImage ||
    trade.coverImage ||
    trade.thumbnailUrl ||
    trade.imageUrl ||
    trade.image ||
    trade.mainImage ||
    (Array.isArray(trade.imageURLs) ? trade.imageURLs[0] : "") ||
    (Array.isArray(trade.imageUrls) ? trade.imageUrls[0] : "") ||
    (Array.isArray(trade.images) ? trade.images[0] : "") ||
    ""
  );
};

const statusLabel = (status) => {
  const s = getOfferStatus({ status });
  const map = {
    pending: "Waiting for seller",
    accepted: "Accepted",
    declined: "Declined",
    withdrawn: "Withdrawn",
    expired: "Expired",
    cancelled: "Cancelled",
    blocked_low_offer: "Blocked â€” too low",
  };
  return map[s] || s;
};

const statusClass = (status) => {
  const s = getOfferStatus({ status });
  if (s === "accepted") return "ok";
  if (s === "pending") return "warn";
  if (
    s === "withdrawn" ||
    s === "declined" ||
    s === "expired" ||
    s === "cancelled" ||
    s === "blocked_low_offer"
  ) {
    return "muted";
  }
  return "muted";
};

const mergeById = (...lists) => {
  const map = new Map();

  lists.flat().forEach((item) => {
    if (item?.id) map.set(item.id, item);
  });

  return Array.from(map.values()).sort(
    (a, b) =>
      (toMillis(b.createdAt || b.updatedAt || b.decidedAt) || 0) -
      (toMillis(a.createdAt || a.updatedAt || a.decidedAt) || 0)
  );
};

function EmptyState({ title, text }) {
  return (
    <div className="myoffers-empty">
      <div className="myoffers-empty__icon">âŒ</div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function OfferCard({
  offer,
  trade,
  mode,
  busy,
  onWithdraw,
  onOpen,
}) {
  const status = getOfferStatus(offer);
  const type = getOfferType(offer);
  const cashAmount = getCashAmount(offer);
  const tradeTitle =
    trade?.title ||
    trade?.name ||
    offer?.tradeTitle ||
    offer?.itemTitle ||
    "Trade item";

  const image = getImageFromTrade(trade);
  const createdAt = offer?.createdAt || offer?.timestamp || offer?.updatedAt;
  const canWithdraw = mode === "sent" && status === "pending";

  return (
    <article className={`myoffers-card myoffers-card--${status}`}>
      <div className="myoffers-card__image">
        {image ? (
          <img src={image} alt={tradeTitle} />
        ) : (
          <div className="myoffers-card__placeholder">NZ</div>
        )}
      </div>

      <div className="myoffers-card__body">
        <div className="myoffers-card__top">
          <div>
            <div className="myoffers-card__eyebrow">
              {mode === "sent" ? "Offer sent" : "Offer received"}
            </div>
            <h3 className="nz-line-clamp-2">{tradeTitle}</h3>
          </div>

          <span className={`myoffers-status myoffers-status--${statusClass(status)}`}>
            {statusLabel(status)}
          </span>
        </div>

        <div className="myoffers-card__details">
          <div>
            <span>Offer type</span>
            <strong>{type === "cash" ? "Cash offer" : type === "trade" ? "Trade offer" : "Offer"}</strong>
          </div>

          {type === "cash" ? (
            <div>
              <span>Amount</span>
              <strong>{fmtMoney(cashAmount)}</strong>
            </div>
          ) : null}

          <div>
            <span>Sent</span>
            <strong>{fmtDate(createdAt)}</strong>
          </div>
        </div>

        {mode === "sent" && status === "pending" ? (
          <p className="myoffers-card__hint">
            The seller has not responded yet. You can withdraw this offer before it is accepted.
          </p>
        ) : null}

        {mode === "sent" && status === "accepted" ? (
          <p className="myoffers-card__hint ok">
            Your offer was accepted. Continue with the trade workflow.
          </p>
        ) : null}

        {mode === "sent" && status === "blocked_low_offer" ? (
            <p className="myoffers-card__hint">
                This offer was not sent to the seller because it was below the minimum offer range for this listing.
            </p>
        ) : null}

        <div className="myoffers-card__actions">
          <button
            type="button"
            className="myoffers-btn myoffers-btn--secondary nz-touch-min"
            onClick={() => onOpen(offer, trade)}
          >
            Open
          </button>

          {canWithdraw ? (
            <button
              type="button"
              className="myoffers-btn myoffers-btn--danger nz-touch-min"
              onClick={() => onWithdraw(offer)}
              disabled={busy}
            >
              {busy ? "Withdrawingâ€¦" : "Withdraw Offer"}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function ActivityList({ items, onOpen }) {
  if (!items.length) {
    return (
      <EmptyState
        title="No recent activity"
        text="Offer and workflow updates will appear here."
      />
    );
  }

  return (
    <div className="myoffers-activity-list">
      {items.map((n) => {
        const title = n.title || "Trade update";
        const msg = n.message || "Open to view details.";
        const when = n.createdAt || n.timestamp || n.updatedAt;
        const tradeId = norm(n.tradeId || n?.meta?.tradeId || "");
        const offerId = norm(n.offerId || n?.meta?.offerId || "");

        return (
          <button
            key={n.id}
            type="button"
            className={`myoffers-activity nz-touch-min ${!n.isRead ? "is-unread" : ""}`}
            onClick={() => onOpen({ tradeId, offerId, type: lower(n.type || "") })}
          >
            <div>
              <strong>{title}</strong>
              <span>{msg}</span>
            </div>
            <em>{fmtDate(when)}</em>
          </button>
        );
      })}
    </div>
  );
}

export default function MyOffers() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("sent");
  const [sentOffers, setSentOffers] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tradesById, setTradesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyOfferId, setBusyOfferId] = useState("");
  const [err, setErr] = useState("");

  const withdrawOffer = useMemo(
    () => functions.httpsCallable("withdrawOffer"),
    []
  );

  const uid = currentUser?.uid || "";
  const email = lower(currentUser?.email || "");

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setErr("");

    const unsubs = [];
    let latestBuyerUid = [];
    let latestFromUid = [];
    let latestCreatedByUid = [];

    const apply = () => {
        setSentOffers(mergeById(latestBuyerUid, latestFromUid, latestCreatedByUid));
        setLoading(false);
    };

    try {
      unsubs.push(
        db.collection("TradeOffers")
          .where("buyerUid", "==", uid)
          .limit(200)
          .onSnapshot(
            (snap) => {
              latestBuyerUid = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              apply();
            },
            (e) => {
              console.error("Sent offers buyerUid query failed:", e);
              setErr("Could not load sent offers.");
              setLoading(false);
            }
          )
      );

      unsubs.push(
        db.collection("TradeOffers")
          .where("fromUid", "==", uid)
          .limit(200)
          .onSnapshot(
            (snap) => {
              latestFromUid = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              apply();
            },
            () => apply()
          )
      );

      unsubs.push(
        db.collection("TradeOffers")
          .where("createdByUid", "==", uid)
          .limit(200)
          .onSnapshot(
            (snap) => {
              latestCreatedByUid = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              apply();
            },
            () => apply()
          )
      );
    } catch (e) {
      console.error(e);
      setErr("Could not attach sent offers listener.");
      setLoading(false);
    }

    return () => unsubs.forEach((fn) => fn && fn());
  }, [uid]);

  useEffect(() => {
    if (!uid) return;

    const unsubs = [];
    let bySellerUid = [];
    let byRecipientUid = [];
    let byToUid = [];

    const apply = () => {
    const merged = mergeById(bySellerUid, byRecipientUid, byToUid).filter(
        (offer) => getOfferStatus(offer) !== "blocked_low_offer"
    );

    setReceivedOffers(merged);
    };

    try {
      unsubs.push(
        db.collection("TradeOffers")
          .where("sellerUid", "==", uid)
          .limit(200)
          .onSnapshot(
            (snap) => {
              bySellerUid = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              apply();
            },
            () => apply()
          )
      );

      unsubs.push(
        db.collection("TradeOffers")
          .where("recipientUid", "==", uid)
          .limit(200)
          .onSnapshot(
            (snap) => {
              byRecipientUid = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              apply();
            },
            () => apply()
          )
      );

      unsubs.push(
        db.collection("TradeOffers")
          .where("toUid", "==", uid)
          .limit(200)
          .onSnapshot(
            (snap) => {
              byToUid = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              apply();
            },
            () => apply()
          )
      );
    } catch (e) {
      console.warn("Received offers listener failed:", e);
    }

    return () => unsubs.forEach((fn) => fn && fn());
  }, [uid]);

  useEffect(() => {
    if (!uid && !email) return;

    const unsubs = [];
    let byUid = [];
    let byEmailLower = [];

    const apply = () => {
      const merged = mergeById(byUid, byEmailLower)
        .filter((n) => {
          const t = lower(n.type || "");
          return (
            t.includes("offer") ||
            t.includes("payment") ||
            t.includes("workflow") ||
            t.includes("prep") ||
            t.includes("inspection") ||
            t.includes("delivery")
          );
        })
        .slice(0, 80);

      setActivity(merged);
    };

    try {
      if (uid) {
        unsubs.push(
          db.collection("Notifications")
            .where("recipientUid", "==", uid)
            .limit(120)
            .onSnapshot(
              (snap) => {
                byUid = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                apply();
              },
              () => apply()
            )
        );
      }

      if (email) {
        unsubs.push(
          db.collection("Notifications")
            .where("recipientEmailLower", "==", email)
            .limit(120)
            .onSnapshot(
              (snap) => {
                byEmailLower = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                apply();
              },
              () => apply()
            )
        );
      }
    } catch (e) {
      console.warn("Activity listener failed:", e);
    }

    return () => unsubs.forEach((fn) => fn && fn());
  }, [uid, email]);

  const allOffers = useMemo(
    () => mergeById(sentOffers, receivedOffers),
    [sentOffers, receivedOffers]
  );

  useEffect(() => {
    const tradeIds = Array.from(
      new Set(allOffers.map(getTradeIdFromOffer).filter(Boolean))
    ).slice(0, 120);

    if (!tradeIds.length) {
      setTradesById({});
      return;
    }

    let cancelled = false;

    (async () => {
      const next = {};

      await Promise.all(
        tradeIds.map(async (tradeId) => {
          try {
            const snap = await db.collection("Trades").doc(tradeId).get();
            if (!cancelled && snap.exists) {
              next[tradeId] = { id: snap.id, ...snap.data() };
            }
          } catch (e) {
            console.warn("Failed to load trade for offer:", tradeId, e);
          }
        })
      );

      if (!cancelled) setTradesById(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [allOffers]);

  const openTradePage = useCallback(
  (tradeId, options = {}) => {
    const safeTradeId = getTradeIdFromOffer({ tradeId }) || norm(tradeId);
    if (!safeTradeId) return;

    const query = new URLSearchParams();

    if (options.tab) query.set("tab", options.tab);
    if (options.offerId) query.set("offerId", options.offerId);
    if (options.from) query.set("from", options.from);

    const qs = query.toString();

    navigate(
      `/trade/${encodeURIComponent(safeTradeId)}${qs ? `?${qs}` : ""}`
    );
  },
  [navigate]
);

 const openOffer = useCallback(
    (offer) => {
      const tradeId = getTradeIdFromOffer(offer);
      if (!tradeId) return;

      const status = getOfferStatus(offer);

      if (status === "accepted") {
        openTradePage(tradeId, {
          tab: "workflow",
          offerId: offer?.id,
          from: "my-offers",
        });
        return;
      }

      openTradePage(tradeId, {
        offerId: offer?.id,
        from: "my-offers",
      });
    },
    [openTradePage]
  );

  const openReceivedOffer = useCallback(
    (offer) => {
      const tradeId = getTradeIdFromOffer(offer);
      const offerId = norm(offer?.id);

      if (!tradeId) return;

      const query = new URLSearchParams();

      if (offerId) query.set("offerId", offerId);
      query.set("from", "my-offers-received");

      navigate(
        `/trade/${encodeURIComponent(tradeId)}/offers?${query.toString()}`
      );
    },
    [navigate]
  );

  const openActivity = useCallback(
    ({ tradeId, offerId }) => {
      if (!tradeId) return;

      openTradePage(tradeId, {
        offerId,
        from: "my-offers-activity",
      });
    },
    [openTradePage]
  );

  const handleWithdraw = useCallback(
    async (offer) => {
      const offerId = offer?.id;
      const tradeId = getTradeIdFromOffer(offer);

      if (!offerId) return;

      const ok = window.confirm(
        "Withdraw this offer? The seller will no longer be able to accept it."
      );

      if (!ok) return;

      setBusyOfferId(offerId);
      setErr("");

      try {
        await withdrawOffer({ offerId, tradeId });
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Could not withdraw offer.");
      } finally {
        setBusyOfferId("");
      }
    },
    [withdrawOffer]
  );

  const counts = useMemo(() => {
    const pendingSent = sentOffers.filter((o) => getOfferStatus(o) === "pending").length;
    const pendingReceived = receivedOffers.filter((o) => getOfferStatus(o) === "pending").length;

    return {
      sent: sentOffers.length,
      received: receivedOffers.length,
      pendingSent,
      pendingReceived,
      activity: activity.length,
    };
  }, [sentOffers, receivedOffers, activity]);

  if (!currentUser) {
    return (
      <div className="myoffers-page">
        <div className="myoffers-shell">
          <EmptyState
            title="Please sign in"
            text="You need to sign in to view your offers."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="myoffers-page">
      <div className="myoffers-shell">
        <header className="myoffers-head">
          <div>
            <div className="myoffers-kicker">Marketplace</div>
            <h1>My Offers</h1>
            <p>
              Track offers you sent, offers you received, and the latest trade activity.
            </p>
          </div>

          <button
            type="button"
            className="myoffers-btn myoffers-btn--secondary nz-touch-min"
            onClick={() => navigate("/market")}
          >
            Back to Marketplace
          </button>
        </header>

        <section className="myoffers-summary">
          <div>
            <span>Sent offers</span>
            <strong>{counts.sent}</strong>
          </div>
          <div>
            <span>Waiting for sellers</span>
            <strong>{counts.pendingSent}</strong>
          </div>
          <div>
            <span>Received offers</span>
            <strong>{counts.received}</strong>
          </div>
          <div>
            <span>Need your response</span>
            <strong>{counts.pendingReceived}</strong>
          </div>
        </section>

        {err ? <div className="myoffers-error">{err}</div> : null}

        <div className="myoffers-tabs" role="tablist" aria-label="Offer tabs">
          {[
            { key: "sent", label: "Offers I Sent", count: counts.sent },
            { key: "received", label: "Offers I Received", count: counts.received },
            { key: "activity", label: "Activity", count: counts.activity },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`myoffers-tab nz-touch-min ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span>{tab.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="myoffers-loading">Loading offersâ€¦</div>
        ) : null}

        {!loading && activeTab === "sent" ? (
          sentOffers.length ? (
            <div className="myoffers-list">
              {sentOffers.map((offer) => {
                const tradeId = getTradeIdFromOffer(offer);
                return (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    trade={tradesById[tradeId]}
                    mode="sent"
                    busy={busyOfferId === offer.id}
                    onWithdraw={handleWithdraw}
                    onOpen={openOffer}
                  />
                );
              })}
            </div>
          ) : (
            <EmptyState
              title="No sent offers yet"
              text="Offers you send to sellers will appear here."
            />
          )
        ) : null}

        {!loading && activeTab === "received" ? (
          receivedOffers.length ? (
            <div className="myoffers-list">
              {receivedOffers.map((offer) => {
                const tradeId = getTradeIdFromOffer(offer);
                return (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    trade={tradesById[tradeId]}
                    mode="received"
                    busy={false}
                    onWithdraw={handleWithdraw}
                    onOpen={openReceivedOffer}
                  />
                );
              })}
            </div>
          ) : (
            <EmptyState
              title="No received offers yet"
              text="Offers from buyers on your listings will appear here."
            />
          )
        ) : null}

        {!loading && activeTab === "activity" ? (
          <ActivityList items={activity} onOpen={openActivity} />
        ) : null}
      </div>
    </div>
  );
}